import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Wifi, WifiOff, CheckCircle, Shield, Battery, Bell, Bluetooth } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import api from '../../api/client';
import './OnboardingWizard.css';

interface Props {
  startStep?: number;
  onClose: () => void;
}

interface AlertCfg {
  id: string;
  device_id: string;
  alert_type: string;
  enabled: boolean;
  threshold: number | null;
  notify_push: boolean;
  notify_sms: boolean;
}

const TOTAL = 7;

export default function OnboardingWizard({ startStep = 1, onClose }: Props) {
  const { t } = useTranslation('onboarding');
  const { t: tc } = useTranslation('common');
  const { user, updateUser } = useAuth();

  const [step, setStep] = useState(startStep);
  const [animDir, setAnimDir] = useState<'left' | 'right'>('left');
  const [deviceOnline, setDeviceOnline] = useState<boolean | null>(null);
  const [voltage, setVoltage] = useState<number | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [configs, setConfigs] = useState<AlertCfg[]>([]);
  const { status: pushStatus, subscribe, isSubscribed } = usePushNotifications();

  // Fetch device status on step 2
  useEffect(() => {
    if (step !== 2) return;
    api.get('/devices/').then((r) => {
      const devs = r.data;
      if (!devs.length) { setDeviceOnline(false); return; }
      const dev = devs[0];
      setDeviceId(dev.id);
      api.get(`/devices/${dev.id}/last-position`).then((pos) => {
        if (pos.data) {
          setDeviceOnline(true);
          const v = pos.data.attributes?.power;
          if (v != null) setVoltage(parseFloat(v));
        } else {
          setDeviceOnline(!!dev.last_seen);
        }
      }).catch(() => setDeviceOnline(false));
    }).catch(() => setDeviceOnline(false));
  }, [step]);

  // Fetch configs on step 3/4
  useEffect(() => {
    if (step !== 3 && step !== 4) return;
    api.get('/alerts/config').then((r) => setConfigs(r.data)).catch(() => {});
  }, [step]);

  const navigate = useCallback((dir: 'next' | 'back') => {
    setAnimDir(dir === 'next' ? 'left' : 'right');
    setStep((s) => dir === 'next' ? Math.min(s + 1, TOTAL) : Math.max(s - 1, 1));
  }, []);

  const toggleConfig = async (alertType: string) => {
    const cfg = configs.find((c) => c.alert_type === alertType);
    if (!cfg) return;
    try {
      const res = await api.post('/alerts/config', { ...cfg, enabled: !cfg.enabled });
      setConfigs((prev) => prev.map((c) => c.id === cfg.id ? res.data : c));
    } catch { /* ignore */ }
  };

  const complete = async () => {
    try {
      await api.patch('/auth/me', { onboarding_completed: true });
      updateUser({ onboarding_completed: true });
    } catch { /* ignore */ }
    onClose();
  };

  const skip = async () => {
    try {
      await api.patch('/auth/me', { onboarding_completed: true });
      updateUser({ onboarding_completed: true });
    } catch { /* ignore */ }
    onClose();
  };

  const configEnabled = (type: string) => configs.find((c) => c.alert_type === type)?.enabled ?? true;

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="ob-step ob-step-center">
            <div className="ob-brand">
              <span className="ob-logo-10">10</span><span className="ob-logo-drive">drive</span>
              <span className="ob-logo-moto">moto</span>
            </div>
            <Shield size={56} className="ob-hero-icon" strokeWidth={1} />
            <h1>{t('step1.title')}</h1>
            <p className="ob-subtitle">{t('step1.subtitle')}</p>
            <button className="ob-btn-primary" onClick={() => navigate('next')}>
              {t('step1.button')}
            </button>
          </div>
        );

      case 2:
        return (
          <div className="ob-step ob-step-center">
            {deviceOnline === null ? (
              <div className="spinner" />
            ) : deviceOnline ? (
              <>
                <Wifi size={56} className="ob-icon-green" strokeWidth={1.5} />
                <h2>{t('step2.online')}</h2>
                {voltage != null && (
                  <p className="ob-voltage">{voltage.toFixed(1)}V</p>
                )}
              </>
            ) : (
              <>
                <WifiOff size={56} className="ob-icon-red" strokeWidth={1.5} />
                <h2>{t('step2.offline')}</h2>
                <p className="ob-subtitle">{t('step2.help')}</p>
              </>
            )}
          </div>
        );

      case 3:
        return (
          <div className="ob-step">
            <Shield size={36} className="ob-section-icon" strokeWidth={1.5} />
            <h2>{t('step3.title')}</h2>
            <div className="ob-toggle-list">
              <div className="ob-toggle-row">
                <div>
                  <p className="ob-toggle-label">{t('step3.towing_label')}</p>
                  <p className="ob-toggle-desc">{t('step3.towing_desc')}</p>
                </div>
                <button
                  className={`toggle-btn ${configEnabled('towing') ? 'on' : 'off'}`}
                  onClick={() => toggleConfig('towing')}
                >
                  {configEnabled('towing') ? 'Wł.' : 'Wył.'}
                </button>
              </div>
              <div className="ob-toggle-row">
                <div>
                  <p className="ob-toggle-label">{t('step3.geofence_label')}</p>
                  <p className="ob-toggle-desc">{t('step3.geofence_desc')}</p>
                </div>
                <button
                  className={`toggle-btn ${configEnabled('geofence_exit') ? 'on' : 'off'}`}
                  onClick={() => toggleConfig('geofence_exit')}
                >
                  {configEnabled('geofence_exit') ? 'Wł.' : 'Wył.'}
                </button>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="ob-step">
            <Battery size={36} className="ob-section-icon" strokeWidth={1.5} />
            <h2>{t('step4.title')}</h2>
            {voltage != null && (
              <p className="ob-voltage-inline">
                {t('step4.current_voltage', { voltage: voltage.toFixed(1) })}
              </p>
            )}
            <div className="ob-toggle-list">
              <div className="ob-toggle-row">
                <p className="ob-toggle-label">{t('step4.voltage_low_label')}</p>
                <button
                  className={`toggle-btn ${configEnabled('voltage_low') ? 'on' : 'off'}`}
                  onClick={() => toggleConfig('voltage_low')}
                >
                  {configEnabled('voltage_low') ? 'Wł.' : 'Wył.'}
                </button>
              </div>
              <div className="ob-toggle-row">
                <p className="ob-toggle-label">{t('step4.voltage_critical_label')}</p>
                <button
                  className={`toggle-btn ${configEnabled('voltage_critical') ? 'on' : 'off'}`}
                  onClick={() => toggleConfig('voltage_critical')}
                >
                  {configEnabled('voltage_critical') ? 'Wł.' : 'Wył.'}
                </button>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="ob-step ob-step-center">
            <Bell size={56} className="ob-section-icon" strokeWidth={1} />
            <h2>{t('step5.title')}</h2>
            <p className="ob-subtitle">{t('step5.description')}</p>
            {isSubscribed ? (
              <div className="ob-success-row">
                <CheckCircle size={20} className="ob-icon-green" />
                <span>Powiadomienia włączone</span>
              </div>
            ) : (
              <button
                className="ob-btn-primary"
                onClick={subscribe}
                disabled={pushStatus === 'loading' || pushStatus === 'denied'}
              >
                {pushStatus === 'loading' ? '...' :
                 pushStatus === 'denied' ? 'Zablokowane w przeglądarce' :
                 t('step5.enable')}
              </button>
            )}
          </div>
        );

      case 6:
        return (
          <div className="ob-step ob-step-center">
            <Bluetooth size={56} className="ob-section-icon" strokeWidth={1} />
            <h2>{t('step6.title')}</h2>
            <p className="ob-subtitle">{t('step6.description')}</p>
            <a
              className="ob-btn-primary"
              href="/docs/instrukcja_pl.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('step6.learn_more')}
            </a>
          </div>
        );

      case 7:
        return (
          <div className="ob-step ob-step-center">
            <CheckCircle size={64} className="ob-icon-green" strokeWidth={1} />
            <h1>{t('step7.title')}</h1>
            <p className="ob-subtitle">{t('step7.subtitle')}</p>
            <button className="ob-btn-primary" onClick={complete}>
              {t('step7.button')}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="ob-overlay">
      <div className="ob-container">
        {/* Header */}
        <div className="ob-header">
          <div className="ob-progress-bar">
            <div className="ob-progress-fill" style={{ width: `${(step / TOTAL) * 100}%` }} />
          </div>
          <div className="ob-header-row">
            <span className="ob-step-counter">{step} / {TOTAL}</span>
            <button className="ob-close" onClick={skip} title={tc('buttons.skip')}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`ob-content ob-anim-${animDir}`} key={step}>
          {renderStep()}
        </div>

        {/* Footer navigation */}
        {step < TOTAL && (
          <div className="ob-footer">
            {step > 1 && (
              <button className="ob-btn-ghost" onClick={() => navigate('back')}>
                {tc('buttons.back')}
              </button>
            )}
            {step === 1 ? null : (
              <button className="ob-btn-secondary" onClick={() => navigate('next')}>
                {tc('buttons.next')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
